import React, { useEffect, useMemo, useState } from 'react';
import moment from 'moment';
import classnames from 'classnames';
import api from 'helpers/api';
import _ from 'underscore';
import ReactTooltip from 'react-tooltip';
import { useDebouncedCallback } from 'use-debounce';
import dateFormatter from 'helpers/date_formatter';
import { useAngularService } from 'helpers/get_angular_service';
import notification from 'helpers/notification';
import capitalize from 'helpers/capitalize';
import { useExtendState } from 'helpers/use_extend_state';
import plural from 'helpers/plural';
import useGymUsers from 'helpers/use_gym_users';
import usePermissions from 'helpers/use_permissions';
import { useResponsive } from 'helpers/use_responsive';
import useEffectSkipInitial from 'helpers/use_effect_skip_initial';
import Button from '../../components/button';
import Dropdown from '../../components/dropdown';
import Link from '../../components/link';
import Table from '../../components/table';
import Checkbox from '../../components/checkbox';
import Input from '../../components/input';
import ContactLink from '../../components/contact_link';
import ExportButton from '../../components/export_button';
import Avatar from '../../components/avatar';
import Icon from '../../components/icon';
import Clipboard from '../../components/clipboard';
import Tooltip from '../../components/tooltip';
import ScrollableFilters from '../../components/scrollable_filters';
import Select from '../../components/select';
import ButtonsSelect from '../../components/buttons_select';
import TableSettings from '../../components/table_settings';
import DateInputRange from 'react/components/date_input_range';
import redirect from 'helpers/redirect';
import useOnMount from 'helpers/use_on_mount';
import useGym from 'helpers/use_gym';
import useMobileAppSettings from 'helpers/use_mobile_app_settings';
import useProspectStatuses from 'helpers/use_prospect_statuses';
import Banner from 'react/components/banner';
import sources from 'helpers/sources';
import { MembershipInterruptionCounts } from 'react/types/interruption_request';
import { openModal, closeModal, setModalHandler } from 'react/services/modal_service';
import ModalContainer from 'react/components/modals/components/modal_container';
import ArchiveContactModal from './archive_contact_modal';
import ConvertContactModal, { getConvertContactModalTitle } from './convert_contact_modal';

interface Filters {
  search: string;
  status?: string;
  owner_id?: string | number;
  mobile_app_enabled?: string;
  behind_on_payments?: string;
  added_after?: string;
  added_before?: string;
  trial_status?: string;
}

type ActiveView = 'list' | 'kanban';

const memberStatuses = [
  { value: 'active', label: 'Active' },
  { value: 'cancelled', label: 'Cancelled' },
  { value: 'suspended', label: 'Suspended' },
  { value: 'active_cancelling', label: 'Active cancelling' },
];

const trialStatuses = [
  { value: 'trial_exists', label: 'Active and expired trials' },
  { value: 'active_trial', label: 'All active trials' },
  { value: 'active_trial_memberships_only', label: 'Active trial (no other membership)' },
  {
    value: 'active_trial_and_regular_memberships',
    label: 'Active trial (other membership present)',
  },
  { value: 'trial_expired', label: 'Expired trials only' },
  { value: 'no_trial', label: 'No trial' },
];

const defaultFilters = {
  search: '',
  status: '',
  missing: true,
  credit_card: true,
  bank_account: true,
  trial_status: '',
};

const ContactsList = () => {
  const $rootScope = useAngularService('$rootScope');
  const $stateParams = useAngularService('$stateParams');
  const $modal = useAngularService('$modal');
  const NewContactService = useAngularService('NewContactService');
  const GTMService = useAngularService('GTMService');
  const PageSettingsService = useAngularService('PageSettingsService');
  const loadingAnimation = useAngularService('loadingAnimation');
  const CwStorage = useAngularService('CwStorage');
  const ReactModal = useAngularService('ReactModal');
  const DrawerService = useAngularService('DrawerService');
  const MasqueradingService = useAngularService('MasqueradingService');
  const TasksService = useAngularService('TasksService');

  const contactType = $stateParams.contactType;
  const gym = useGym();
  const isMasquerading = MasqueradingService.isMasquerading();
  const permissions = usePermissions();
  const [membersCount, setMembersCount] = useState($rootScope.membersCount);
  const [prospectsCount, setProspectsCount] = useState($rootScope.prospectsCount);
  const [nacCount, setNacCount] = useState($rootScope.nacCount);
  const [contacts, setContacts] = useState([]);
  const [selectedContacts, setSelectedContacts] = useState([]);
  const [page, setPage] = useState(1);
  const [contactsLoading, setContactsLoading] = useState(false);
  const [pageSettingsVisible, togglePageSettings] = useState(false);
  const [membershipInterruptions, setMembershipInterruptions] = useState<
    MembershipInterruptionCounts
  >({
    pending_count: 0,
    pending_cancellations_count: 0,
    pending_suspensions_count: 0,
  });
  const [filters, extendFilters] = useExtendState<Filters>(() => {
    try {
      const savedFilters = JSON.parse(CwStorage.getItem(`gym_${gym.id}_${contactType}_filters`));
      return savedFilters || defaultFilters;
    } catch (error) {
      return defaultFilters;
    }
  });

  const prospectStatuses = useProspectStatuses();

  const prospectStatusesWithEmptyOption = useMemo(() => {
    const emptyOption = {
      label: '(No status set)',
      value: 'no_prospect_status',
    };
    const invalidOption = {
      label: '(Invalid status)',
      value: 'invalid_prospect_status',
    };
    return prospectStatuses.length
      ? [emptyOption, invalidOption, ...prospectStatuses]
      : prospectStatuses;
  }, [prospectStatuses]);

  const [pageSize, _setPageSize] = useState(0);
  const [activeView, setActiveView] = useState<ActiveView>('list');
  const { isMobile } = useResponsive();
  const mobileAppSettings = useMobileAppSettings();

  const interruptionRequestsAvailable =
    gym.mobile_app_enabled &&
    mobileAppSettings != null &&
    (mobileAppSettings.custom_app ||
      !mobileAppSettings.features_disabled?.includes('cancellation_requests'));

  const saveFilters = useDebouncedCallback(() => {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { search, ...filtersRest } = filters;
    if ($stateParams.defaultFilter) return;
    CwStorage.setItem(`gym_${gym.id}_${contactType}_filters`, JSON.stringify(filtersRest));
  }, 500);

  useEffectSkipInitial(() => {
    saveFilters();
  }, [filters]);

  const bankAccountLabel = useMemo(() => {
    if (gym.bank_account_provider === 'bluefin') return 'Check';
    if (gym.country === 'US') return 'Checking account';
    if (gym.country === 'GB' && gym.bank_account_provider === 'gocardless') return 'Active mandate';
    return 'Bank account';
  }, [gym.bank_account_provider, gym.country]);

  const users = useGymUsers();

  const paymentMethodOptions = useMemo(() => {
    return _.compact([
      gym.credit_card_provider && { value: 'credit_card', label: 'Card' },
      gym.bank_account_provider && { value: 'bank_account', label: bankAccountLabel },
      { value: 'missing', label: 'Missing' },
    ]);
  }, [bankAccountLabel, gym.bank_account_provider, gym.credit_card_provider]);

  const isMembers = () => {
    return contactType === 'members';
  };

  const isProspects = () => {
    return contactType === 'prospects';
  };

  const isNac = () => {
    return contactType === 'non_attending_contacts';
  };

  const [sorting, setSortings] = useState(null);

  const getPageId = () => {
    return isMembers() ? 'member_list' : isProspects() ? 'prospect_list' : 'nac_list';
  };

  const getPageSettings = () => {
    const pageId = getPageId();
    return PageSettingsService.settings[pageId];
  };

  const [pageSettings, setPageSettings] = useState(getPageSettings());

  useEffect(() => {
    if (!pageSettings) return;

    const size = isMasquerading
      ? +CwStorage.getItem(`gym_${gym.id}_${contactType}_page_size`)
      : pageSettings.page_size;
    _setPageSize(size || 10);

    let preferredOrderBy;
    let preferredOrderDirection;

    if (isMasquerading) {
      preferredOrderBy = CwStorage.getItem(`gym_${gym.id}_${contactType}_order_by`);
      preferredOrderDirection = CwStorage.getItem(`gym_${gym.id}_${contactType}_order_direction`);
    } else {
      preferredOrderBy = pageSettings.order_column_name;
      preferredOrderDirection = pageSettings.order_direction;
    }

    const key = isProspects() ? preferredOrderBy || 'created_at' : preferredOrderBy || 'last_name';
    const dir = isProspects()
      ? preferredOrderDirection || 'desc'
      : preferredOrderDirection || 'asc';

    if (!sorting || sorting[key] !== dir) {
      setSortings({ [key]: dir });
    }
  }, [pageSettings]);

  const loadPageSettings = () => {
    loadingAnimation.show();
    const pageId = getPageId();
    PageSettingsService.load(pageId, { withMeta: true });
  };

  const formattedContactsType = () => {
    const type = isNac() ? 'non-attending' : contactType;
    return capitalize(type);
  };

  const setPageSize = (count: string | number) => {
    _setPageSize(+count);
    if (isMasquerading) {
      CwStorage.setItem(`gym_${gym.id}_${contactType}_page_size`, count);
    } else {
      PageSettingsService.saveSettings(getPageId(), { page_size: +count });
    }
  };

  const getCounters = () => {
    if (!isMembers()) {
      const url = `gyms/${gym.id}/members`;
      api.get(url, { count: 0 }).then((response) => {
        setMembersCount(response.meta.total);
        $rootScope.membersCount = response.meta.total;
      });
    }

    if (!isProspects()) {
      const url = `gyms/${gym.id}/prospects`;
      api.get(url, { count: 0 }).then((response) => {
        setProspectsCount(response.meta.total);
        $rootScope.prospectsCount = response.meta.total;
      });
    }

    if (!isNac()) {
      const url = `gyms/${gym.id}/non_attending_contacts`;
      api.get(url, { count: 0 }).then((response) => {
        setNacCount(response.meta.total);
        $rootScope.nacCount = response.meta.total;
      });
    }
  };

  useOnMount(() => {
    loadPageSettings();
    GTMService.trackPage(formattedContactsType());
    getCounters();
    CwStorage.setItem('defaultContactList', contactType);
    $rootScope.$emit('contacts:list:opened');

    const unsubscribe = $rootScope.$on('page_settings:updated', () => updateSettings());
    return unsubscribe;
  });

  const updateSettings = () => {
    const settings = getPageSettings();

    if (settings) {
      setPageSettings(settings);
    }
  };

  const onAddNewContactClicked = (action) => {
    NewContactService.clear();
    track(action);
  };

  const track = (action) => {
    GTMService.trackPage(formattedContactsType(), action);
  };

  const getCsv = () => {
    $modal.open({
      template: require('templates/gyms/contacts/export_contacts_modal.html.slim'),
      resolve: {
        url: () => `/gyms/${gym.id}/${contactType}/export.csv`,
        params: () => {
          const { search, ...filtersRest } = filters;
          return { search, filters: filtersRest, order: sorting };
        },
      },
      controller: 'ExportContactsCtrl',
    });
  };

  const onContactStatusSelect = (status, contact) => {
    updateProspectsStatus({ ids: [contact.id], status });
  };

  const updateProspectsStatus = ({ ids, status }) => {
    const url = '/update_contacts_status';
    const params = {
      gym_id: gym.id,
      ids,
      status,
    };
    api.put(url, params).then(
      (response) => {
        notification.success(response.success);
        reload();
      },
      () => {
        notification.error('Request could not be completed. Please try again');
      },
    );
  };

  const bulkChangeStatus = (status) => {
    const count = selectedContacts.length;
    if (window.confirm(`Change status for ${count} selected ${plural('contacts', count)}?`)) {
      const ids = selectedContacts.map((contact) => contact.id);
      updateProspectsStatus({ ids, status });
    }
  };

  const bulkConvertToNacs = () => {
    const conversionType = isMembers() ? 'member_to_nac' : 'prospect_to_nac';

    openModal({
      component: ConvertContactModal,
      props: {
        title: getConvertContactModalTitle(),
        contacts: selectedContacts,
        conversionType,
        cancel: closeModal,
        submitModal: () => {
          closeModal();
          setSelectedContacts([]);
          reload();
          getCounters();
        },
      },
    });
  };

  const bulkConvertToMembers = () => {
    const conversionType = isProspects() ? 'prospect_to_member' : 'nac_to_member';

    openModal({
      component: ConvertContactModal,
      props: {
        title: getConvertContactModalTitle(),
        contacts: selectedContacts,
        conversionType,
        cancel: closeModal,
        submitModal: () => {
          closeModal();
          setSelectedContacts([]);
          reload();
          getCounters();
        },
      },
    });
  };

  const bulkConvertToProspects = () => {
    openModal({
      component: ConvertContactModal,
      props: {
        title: getConvertContactModalTitle(),
        contacts: selectedContacts,
        conversionType: 'nac_to_prospect',
        cancel: closeModal,
        submitModal: () => {
          closeModal();
          setSelectedContacts([]);
          reload();
          getCounters();
        },
      },
    });
  };

  const convertToMember = (contact) => {
    const conversionType = isProspects() ? 'prospect_to_member' : 'nac_to_member';

    openModal({
      component: ConvertContactModal,
      props: {
        title: getConvertContactModalTitle(),
        contact,
        conversionType,
        cancel: closeModal,
        submitModal: () => {
          closeModal();
          reload();
          getCounters();
        },
      },
    });
  };

  const convertToProspect = (contact) => {
    openModal({
      component: ConvertContactModal,
      props: {
        title: getConvertContactModalTitle(),
        contact,
        conversionType: 'nac_to_prospect',
        cancel: closeModal,
        submitModal: () => {
          closeModal();
          reload();
          getCounters();
        },
      },
    });
  };

  const convertToNac = (contact) => {
    const conversionType = isProspects() ? 'prospect_to_nac' : 'member_to_nac';

    openModal({
      component: ConvertContactModal,
      props: {
        title: getConvertContactModalTitle(),
        contact,
        conversionType,
        cancel: closeModal,
        submitModal: () => {
          closeModal();
          reload();
          getCounters();
        },
      },
    });
  };

  const reload = () => {
    getContacts(page);
  };

  const getContacts = (page) => {
    loadingAnimation.show();
    setPage(page);
    setContactsLoading(true);
    const url = `gyms/${gym.id}/${contactType}`;

    const { search, ...filtersRest } = filters;

    api
      .get(url, { page, count: pageSize, search, filters: filtersRest, order: sorting, people: 1 })
      .then(
        (data) => {
          setContacts(data);
          setSelectedContacts([]);
          setContactsLoading(false);
          loadingAnimation.hide();

          if (isMembers()) {
            setMembersCount(data.meta.total);
            $rootScope.membersCount = data.meta.total;
          }

          if (isProspects()) {
            setProspectsCount(data.meta.total);
            $rootScope.prospectsCount = data.meta.total;
          }

          if (isNac()) {
            setNacCount(data.meta.total);
            $rootScope.nacCount = data.meta.total;
          }
        },
        () => {
          notification.error('Request could not be completed. Please try again');
          loadingAnimation.hide();
          setContactsLoading(false);
        },
      );
  };

  const onActionSelect = (action, contact) => {
    switch (action) {
      case 'convert_to_member': {
        convertToMember(contact);
        break;
      }

      case 'convert_to_prospect': {
        convertToProspect(contact);
        break;
      }

      case 'convert_to_nac': {
        convertToNac(contact);
        break;
      }
      case 'add_task': {
        addTask(contact);
        break;
      }

      case 'add_flag': {
        addFlag(contact);
        break;
      }

      case 'delete': {
        confirmDeleteContact(contact);
        break;
      }

      case 'merge_contact': {
        mergeContact(contact);
        break;
      }

      default:
        break;
    }
  };

  const isContactSelected = (contact) => {
    return _.find(selectedContacts, (selected) => selected.id === contact.id);
  };

  const toggleContact = (contact) => {
    if (isContactSelected(contact)) {
      setSelectedContacts(_.reject(selectedContacts, (selected) => selected.id === contact.id));
    } else {
      setSelectedContacts([...selectedContacts, contact]);
    }
  };

  useEffect(() => {
    const isFilterTrial =
      $stateParams.defaultFilter === 'active_trial' ||
      $stateParams.defaultFilter === 'trial_expired';
    if ($stateParams.defaultFilter === 'suspended') {
      extendFilters({ status: $stateParams.defaultFilter, trial_status: 'all' });
      return;
    }
    if ($stateParams.defaultFilter && !isFilterTrial) {
      extendFilters({ status: $stateParams.defaultFilter, trial_status: 'no_trial' });
    }
    if ($stateParams.defaultFilter && isFilterTrial) {
      extendFilters({ trial_status: $stateParams.defaultFilter, status: '' });
    }
  }, [$stateParams]);

  const toggleAll = () => {
    if (selectedContacts.length) {
      setSelectedContacts([]);
    } else {
      setSelectedContacts(_.uniq(selectedContacts.concat(contacts), (item) => item.id));
    }
  };

  const isAllContactsSelected = () => {
    return !!contacts.length && _.every(contacts, (contact) => isContactSelected(contact));
  };

  const getColumnCell = (columnName) => {
    const columns = {
      name: ({ row }) => (
        <div className="flex align-center contacts-page__contact-name">
          <Avatar image={row.original.image_url} name={row.original.first_name} />
          <ContactLink
            id={row.original.id}
            firstName={row.original.first_name}
            lastName={row.original.last_name}
            type={row.original.type}
            hideBadge
          />

          {row.original.contact_flags.length > 0 && (
            <Button
              variant="clear"
              tooltip="Contact has been flagged"
              onClick={() => displayFlags(row.original)}
            >
              <Icon name="flag" width={12} height={14} color="#F26F21" />
            </Button>
          )}
          {!isProspects() ? (
            <>
              {row.original.good_standing === true && (
                <Button variant="clear" tooltip="Payments up to date">
                  <Icon name="tick-circle" color="#43B837" width={18} height={18} />
                </Button>
              )}

              {row.original.good_standing === false && (
                <Button variant="clear" tooltip="Behind on payments">
                  <Icon name="mark-circle" width={16} height={16} color="#ED0000" />
                </Button>
              )}
            </>
          ) : null}

          {row.original.dob && moment(row.original.dob).format('M_D') === moment().format('M_D') && (
            <Button variant="clear" tooltip={`Today is ${row.original.first_name}'s birthday!`}>
              <Icon name="cake" color="#2197F3" width={18} height={18} />
            </Button>
          )}
        </div>
      ),
      status: ({ row }) =>
        isProspects() && permissions.manage_contacts ? (
          <Dropdown
            options={prospectStatuses}
            onSelect={(action) => onContactStatusSelect(action, row.original)}
            buttonVariant="outline-primary-rounded"
            placeholder="Select status"
            value={row.original.status}
          />
        ) : (
          <div
            className={classnames('status-badge', {
              'status-badge--active': row.original.status === 'Active',
              'status-badge--cancelled': row.original.status === 'Cancelled',
              'status-badge--suspended': row.original.status === 'Suspended',
            })}
          >
            {row.original.status}
          </div>
        ),
      created_at: ({ row }) => <div>{dateFormatter(row.original.created_at)}</div>,
      payment_method: ({ row }) => (
        <div>
          {row.original.payment_method?.toLowerCase() === 'bank account'
            ? bankAccountLabel
            : row.original.payment_method}
        </div>
      ),
      last_attended: ({ row }) => <div>{dateFormatter(row.original.last_attended)}</div>,
      email: ({ row }) => (
        <div>
          <Clipboard text={row.original.email}>{row.original.email}</Clipboard>
        </div>
      ),
      mobile_app_enabled: ({ row }) => <div>{row.original.mobile_app_enabled ? 'Yes' : 'No'}</div>,
      last_contacted: ({ row }) => <div>{dateFormatter(row.original.last_contacted)}</div>,
      last_booking_scheduled: ({ row }) => (
        <div>{dateFormatter(row.original.last_booking_scheduled, 'MMM D YYYY, h:mma')}</div>
      ),
      next_booking_scheduled: ({ row }) => (
        <div>{dateFormatter(row.original.next_booking_scheduled, 'MMM D, h:mma')}</div>
      ),
      trial_status: ({ row }) => <div>{row.original.trial_status}</div>,
      phone_number: ({ row }) => <div>{row.original.phone_number}</div>,
      address: ({ row }) => <div>{row.original.address}</div>,
      first_activated: ({ row }) => <div>{dateFormatter(row.original.first_activated)}</div>,
      first_attended: ({ row }) => <div>{dateFormatter(row.original.first_attended)}</div>,
      member_number: ({ row }) => <div>{row.original.member_number}</div>,
      dob: ({ row }) => <div>{row.original.dob}</div>,
      age: ({ row }) => <div>{row.original.age}</div>,
      owner_name: ({ row }) => <div>{row.original.owner_name}</div>,
      location_name: ({ row }) => <div>{row.original.location_name}</div>,
      family_summary: ({ row }) => (
        <div>
          {row.original.family_summary.map((item, index) => (
            <div key={index}>{item}</div>
          ))}
        </div>
      ),
    };

    const renderDefault = ({ row }) => <div>{row.original[columnName]}</div>;

    return columns[columnName] || renderDefault;
  };

  const toggleSorting = (columnName: string, val: string) => {
    if (isMasquerading) {
      CwStorage.setItem(`gym_${gym.id}_${contactType}_order_by`, columnName);
      CwStorage.setItem(`gym_${gym.id}_${contactType}_order_direction`, val);
    } else {
      PageSettingsService.saveSettings(getPageId(), {
        order_column_name: columnName,
        order_direction: val,
      });
    }
    setSortings({ [columnName]: val });
  };

  const getCurrentSortingVal = () => {
    if (!sorting) return '';
    const key = _.keys(sorting)[0];
    const val = sorting[key];
    return `${key}-${val}`;
  };

  const getColumnHeader = (column) => {
    if (!sorting) return null;
    if (!column.sortable) return column.label;

    if (column.name === 'name') {
      return (
        <Dropdown
          options={[
            { label: 'First name A-Z', value: 'first_name-asc' },
            { label: 'First name Z-A', value: 'first_name-desc' },
            { label: 'Last name A-Z', value: 'last_name-asc' },
            { label: 'Last name Z-A', value: 'last_name-desc' },
          ]}
          onSelect={(val) => {
            const [columnName, order] = val.split('-');
            toggleSorting(columnName, order);
          }}
          value={getCurrentSortingVal()}
          buttonVariant="clear"
        >
          <span
            className={classnames('sortable-column', {
              'sortable-column--active': sorting['first_name'] || sorting['last_name'],
            })}
          >
            {column.label}
            {sorting['first_name'] || sorting['last_name'] ? (
              <i
                className={classnames('margin-left-8', {
                  'icon-chevron-down': (sorting['first_name'] || sorting['last_name']) === 'desc',
                  'icon-chevron-up': (sorting['first_name'] || sorting['last_name']) === 'asc',
                })}
              ></i>
            ) : (
              <i className="icon-chevron-down margin-left-8"></i>
            )}
          </span>
        </Dropdown>
      );
    }

    return (
      <Button
        variant="clear"
        onClick={() => {
          const newVal = sorting[column.name] === 'asc' ? 'desc' : 'asc';

          toggleSorting(column.name, newVal);
        }}
      >
        <span
          className={classnames('sortable-column', {
            'sortable-column--active': sorting[column.name],
          })}
        >
          {column.label}
          {sorting[column.name] ? (
            <i
              className={classnames('margin-left-8', {
                'icon-chevron-down': sorting[column.name] === 'desc',
                'icon-chevron-up': sorting[column.name] === 'asc',
              })}
            ></i>
          ) : (
            <i className="icon-chevron-down margin-left-8"></i>
          )}
        </span>
      </Button>
    );
  };

  const getColumnDetails = () => {
    if (!pageSettings?.columns?.length) return [];

    return pageSettings.columns
      .filter((column) => column.display)
      .map((column) => ({
        Header: getColumnHeader(column),
        id: column.name,
        Cell: getColumnCell(column.name),
      }));
  };

  const mobileAppColumnVisible = useMemo(
    () =>
      _.find(
        pageSettings?.columns,
        (column) => column.display && column.name === 'mobile_app_enabled',
      ),
    [pageSettings],
  );

  const displayFlags = (contact) => {
    ReactModal.open({
      component: 'ContactFlagsModal',
      props: {
        contact,
      },
    });
  };

  const addTask = (contact) => {
    TasksService.create({ contactId: contact.id });
  };

  const addFlag = (contact) => {
    ReactModal.open({
      component: 'AddContactFlagModal',
      drawer: true,
      props: {
        contact,
      },
    }).then(() => {
      reload();
    });
  };

  const addNote = (contact) => {
    ReactModal.open({
      component: 'AddContactNoteModal',
      drawer: true,
      props: {
        contact,
      },
    });
  };

  const confirmDeleteContacts = () => {
    openModal({
      component: ArchiveContactModal,
      props: {
        title: 'Archive Contacts',
        contacts: selectedContacts,
        cancel: closeModal,
        submitModal: () => {
          closeModal();
          setSelectedContacts([]);
          reload();
        },
      },
    });
  };

  const mergeContact = (contact) => {
    redirect('gym.contacts.merge', {
      contactId: contact.id,
      gymId: gym.id,
    });
  };

  const deleteContact = (contact) => {
    const url = `gyms/${gym.id}/${contactType}/${contact.id}`;
    return api.delete(url);
  };

  const confirmDeleteContact = (contact) => {
    openModal({
      component: ArchiveContactModal,
      props: {
        title: 'Archive this contact?',
        contact,
        cancel: closeModal,
        submitModal: () => {
          closeModal();
          reload();
        },
      },
    });
  };

  const useNewEditor = useMemo(() => {
    return gym.gym_config.use_slate_editor;
  }, [gym]);

  const sendMessageOldEditor = (contact) => {
    DrawerService.open({
      template: require('templates/gyms/contacts/send_message_modal.html.slim'),
      resolve: {
        messageParams: () => 'contact',
        contactId: () => contact.id,
        messageType: () => 'email',
      },
      controller: 'SendMessageModalCtrl',
    });
  };

  const sendMessageReactEditor = (contact) => {
    ReactModal.open({
      component: 'ContactMessageSender',
      drawer: true,
      props: {
        contact,
      },
    });
  };

  const sendBulkMessage = (contacts) => {
    DrawerService.open({
      template: require('templates/gyms/contacts/send_bulk_message_modal.html.slim'),
      resolve: {
        contacts: () => contacts,
        recipientIds: () => [],
        defaultChannel: () => 'email',
        bulkMessageParams: () => ({
          source: 'people_list',
        }),
        existingTemplate: () => null,
      },
      controller: 'SendBulkMessageModalCtrl',
    });
  };

  const getColumns = () => {
    const customColumns = getColumnDetails();
    if (!customColumns?.length) return [];

    setTimeout(() => {
      ReactTooltip.rebuild();
    }, 1000);

    return _.compact([
      {
        Header: (
          <Checkbox
            checked={isAllContactsSelected()}
            intermediate={selectedContacts.length && !isAllContactsSelected()}
            onChange={() => toggleAll()}
            variant="v2"
          />
        ),
        id: 'select',
        Cell: ({ row }) => (
          <Checkbox
            checked={isContactSelected(row.original)}
            onChange={() => {
              toggleContact(row.original);
            }}
            variant="v2"
          />
        ),
      },
      ...customColumns,
      {
        Header: '',
        id: 'actions',
        Cell: ({ row }) => (
          <div className="text-right">
            {permissions.send_comms && (
              <Button
                variant="icon--v2"
                onClick={() =>
                  useNewEditor
                    ? sendMessageReactEditor(row.original)
                    : sendMessageOldEditor(row.original)
                }
                tooltip="Send message"
              >
                <Icon name="message-2" width={20} height={20} />
              </Button>
            )}

            {permissions.view_contact_profile_page && (
              <Button variant="icon--v2" onClick={() => addNote(row.original)} tooltip="Add note">
                <Icon name="note" width={20} height={20} />
              </Button>
            )}

            <Dropdown
              options={_.compact([
                !isMembers() &&
                  permissions.manage_contacts && {
                    value: 'convert_to_member',
                    label: 'Convert to Member',
                  },
                isNac() &&
                  permissions.manage_contacts && {
                    value: 'convert_to_prospect',
                    label: 'Convert to Prospect',
                  },
                !isNac() &&
                  permissions.manage_contacts && {
                    value: 'convert_to_nac',
                    label: 'Convert to NAC',
                  },
                permissions.manage_tasks && {
                  value: 'add_task',
                  label: 'Add task',
                },

                permissions.manage_contact_flags && {
                  value: 'add_flag',
                  label: 'Add flag',
                },
                permissions.delete_contacts && {
                  value: 'delete',
                  label: `Archive ${isMembers() ? 'member' : isProspects() ? 'prospect' : 'NAC'}`,
                },
                permissions.merge_contacts && {
                  value: 'merge_contact',
                  label: 'Merge Contact',
                },
              ])}
              onSelect={(action) => onActionSelect(action, row.original)}
              buttonType="icon-more"
            />
          </div>
        ),
      },
    ]);
  };

  const columns = useMemo(() => getColumns(), [
    selectedContacts,
    pageSettings,
    prospectStatuses,
    sorting,
  ]);

  const bulkMobileAppChangesAvailable = () => {
    return (
      permissions.perform_bulk_mobile_app_operations &&
      gym.mobile_app_enabled &&
      mobileAppSettings?.review_status === 'review_approved'
    );
  };

  const bulkChangesOptions = _.compact([
    permissions.perform_bulk_membership_creations && {
      label: 'Bulk Add Memberships',
      value: 'gym.reports.bulk_add_memberships',
    },
    permissions.perform_bulk_cancellations && {
      label: 'Bulk Cancel Memberships',
      value: 'gym.reports.new_bulk_cancellation',
    },
    permissions.perform_bulk_suspensions && {
      label: 'Bulk Suspend Contacts',
      value: 'gym.reports.bulk_suspend_contacts',
    },
    bulkMobileAppChangesAvailable() && {
      label: 'Bulk Mobile App Changes',
      value: 'gym.reports.bulk_mobile_app_changes',
    },
    permissions.perform_bulk_member_portal_operations &&
      gym.member_portal_enabled && {
        label: 'Bulk Member Portal Changes',
        value: 'gym.reports.bulk_member_portal_operations',
      },
  ]);

  const getInterruptionRequestsCount = async () => {
    loadingAnimation.show();
    const res = await api.get(sources.getMembershipInterruptionRequestCount(gym.id));
    setMembershipInterruptions(res);
    $rootScope.$emit('contacts:states:reload');
    loadingAnimation.hide();
  };

  useEffect(() => {
    if (!permissions.review_membership_interruption_requests) return;
    if (!interruptionRequestsAvailable) return;
    getInterruptionRequestsCount();
  }, [interruptionRequestsAvailable]);

  return (
    <div className="contacts-page">
      <div className="page-header page-header--mobile-column">
        <div className="page-header__tabs">
          <Link
            className={classnames('page-header__item', {
              'page-header__item--active': isMembers(),
            })}
            route="gym.contacts.list"
            routeParams={{ contactType: 'members' }}
          >
            Members
            {membersCount > 0 && <div className="page-header__item-counter">{membersCount}</div>}
          </Link>
          <Link
            className={classnames('page-header__item', {
              'page-header__item--active': isProspects(),
            })}
            route="gym.contacts.list"
            routeParams={{ contactType: 'prospects' }}
          >
            Prospects
            {prospectsCount > 0 && (
              <div className="page-header__item-counter">{prospectsCount}</div>
            )}
          </Link>

          <Link
            className={classnames('page-header__item', { 'page-header__item--active': isNac() })}
            route="gym.contacts.list"
            routeParams={{ contactType: 'non_attending_contacts' }}
          >
            Non-attending
            {nacCount > 0 && <div className="page-header__item-counter">{nacCount}</div>}
          </Link>
        </div>

        <div className="page-header__buttons">
          <Dropdown
            options={bulkChangesOptions}
            onSelect={(state) => redirect(state)}
            placeholder="Bulk changes"
            buttonVariant="outline-primary"
          />
          {permissions.manage_contacts && (
            <>
              {isMembers() && (
                <Link
                  type="button"
                  variant="primary--v2"
                  onClick={() => onAddNewContactClicked('Add a Member')}
                  route="gym.contacts.new"
                >
                  <i className="icon-plus" /> <span>Member</span>
                </Link>
              )}
              {isProspects() && (
                <Link
                  type="button"
                  variant="primary--v2"
                  onClick={() => onAddNewContactClicked('Add a Prospect')}
                  route="gym.contacts.new"
                >
                  <i className="icon-plus" /> <span>Prospect</span>
                </Link>
              )}
              {isNac() && (
                <Link
                  type="button"
                  variant="primary--v2"
                  onClick={() => onAddNewContactClicked('Add a NAC')}
                  route="gym.contacts.new"
                >
                  <i className="icon-plus" /> <span>NAC</span>
                </Link>
              )}
            </>
          )}
        </div>
      </div>

      <div className="container container--no-padding-top">
        {interruptionRequestsAvailable &&
          (isMembers() || isNac()) &&
          (() => {
            const cancelCount = membershipInterruptions.pending_cancellations_count;
            const suspendCount = membershipInterruptions.pending_suspensions_count;

            if (!cancelCount && !suspendCount) return null;

            let desc: JSX.Element;

            if (cancelCount && suspendCount) {
              desc = (
                <>
                  You have <strong>{plural('cancel request', cancelCount, true)}</strong> and <strong>{plural('suspension request', suspendCount, true)}</strong> pending approval
                </>
              );
            } else if (cancelCount) {
              desc = (
                <>
                  You have <strong>{plural('cancel request', cancelCount, true)}</strong> pending approval
                </>
              );
            } else {
              desc = (
                <>
                  You have <strong>{plural('suspension request', suspendCount, true)}</strong> pending approval
                </>
              );
            }
            return (
              <Banner
                icon="pi-info-circle"
                description={desc}
                buttonText="Review requests"
                variant="info"
                buttonVariant="link"
                noMarginBottom
                onClick={() =>
                  redirect(`gym.contacts.membershipInterruptionRequests`, {
                    gymId: gym.id,
                    from: contactType,
                  })
                }
              />
            );
          })()}
        <ScrollableFilters>
          {isMembers() && (
            <>
              <div className="scrollable-filters__item">
                <label>Membership status</label>
                <ButtonsSelect
                  value={filters.status}
                  options={memberStatuses}
                  onChange={(status) => extendFilters({ status })}
                  emptyLabel="All"
                />
              </div>
            </>
          )}

          {(isMembers() || isNac()) && gym.has_payment_provider && (
            <div className="scrollable-filters__item">
              <label>Payment type</label>
              <ButtonsSelect
                value={filters}
                options={paymentMethodOptions}
                onChange={(paymentType) => extendFilters(paymentType)}
                emptyLabel="All"
                multiple
              />
            </div>
          )}

          {isMembers() && mobileAppColumnVisible && (
            <div className="scrollable-filters__item">
              <label>On App</label>
              <Select
                value={filters.mobile_app_enabled}
                options={[
                  {
                    value: 'true',
                    label: 'Yes',
                  },
                  {
                    value: 'false',
                    label: 'No',
                  },
                ]}
                onChange={(mobile_app_enabled) => extendFilters({ mobile_app_enabled })}
                allowEmptyOption
                emptyLabel="All"
                variant="v2"
                width={158}
                size="sm"
              />
            </div>
          )}

          {(isMembers() || isNac()) && (
            <div className="scrollable-filters__item">
              <label>Behind on payments?</label>
              <Select
                value={filters.behind_on_payments}
                options={[
                  {
                    value: 'false',
                    label: 'No',
                  },
                  {
                    value: 'true',
                    label: 'Yes',
                  },
                ]}
                onChange={(behind_on_payments) => extendFilters({ behind_on_payments })}
                allowEmptyOption
                emptyLabel="All"
                variant="v2"
                width={168}
                size="sm"
              />
            </div>
          )}
          {isMembers() && (
            <div className="scrollable-filters__item">
              <label>Trial status</label>
              <Select
                value={filters.trial_status}
                options={trialStatuses}
                onChange={(trial_status) => extendFilters({ trial_status })}
                allowEmptyOption
                emptyLabel="All"
                variant="v2"
                width={164}
                size="sm"
              />
            </div>
          )}
          {isProspects() && (
            <>
              <div className="scrollable-filters__item">
                <label>Status</label>
                <Select
                  value={filters.status}
                  options={prospectStatusesWithEmptyOption}
                  onChange={(status) => extendFilters({ status })}
                  allowEmptyOption
                  variant="v2"
                  width={164}
                  size="sm"
                  emptyLabel="All"
                />
              </div>

              {permissions.manage_other_contacts && (
                <div className="scrollable-filters__item">
                  <label>Owner</label>
                  <Select
                    value={filters.owner_id}
                    options={users}
                    onChange={(owner_id) => extendFilters({ owner_id })}
                    getLabel={(item) => item.name}
                    getValue={(item) => item.id}
                    allowEmptyOption
                    variant="v2"
                    width={161}
                    size="sm"
                    emptyLabel="Show all"
                  />
                </div>
              )}

              {prospectStatuses.length > 0 && (
                <div className="scrollable-filters__item">
                  <label>View</label>
                  <Select
                    value={activeView}
                    options={[
                      { value: 'list', label: 'List' },
                      { value: 'kanban', label: 'Kanban' },
                    ]}
                    onChange={(view: ActiveView) => {
                      setActiveView(view);
                      if (view === 'kanban') {
                        redirect('gym.contacts.kanban');
                      }
                    }}
                    getLabel={(item) => item.label}
                    getValue={(item) => item.value}
                    variant="v2"
                    width={161}
                    size="sm"
                  />
                </div>
              )}

              <div className="table-header-filters__item">
                <label>Added</label>
                <DateInputRange
                  startDate={filters.added_after}
                  endDate={filters.added_before}
                  onChange={(val) => {
                    extendFilters({ added_after: val.startDate, added_before: val.endDate });
                  }}
                  variant="36"
                  appendToBody
                />
              </div>
            </>
          )}
        </ScrollableFilters>

        {!pageSettingsVisible ? (
          pageSettings?.columns && pageSize && sorting ? (
            <>
              <div className="table-wrapper">
                <div className="table-header">
                  <Input
                    value={filters.search}
                    onChange={(search) => extendFilters({ search })}
                    placeholder={isMobile ? 'Search' : 'Search by contact name'}
                  />

                  {permissions.view_contact_profile_page && (
                    <button
                      type="button"
                      onClick={() => togglePageSettings(true)}
                      className="table-header__settings"
                    >
                      <i className="icon-gear" />
                      {!isMobile && <span>Customize</span>}
                    </button>
                  )}
                  <ExportButton onExport={() => getCsv()} variant="table-header" />
                </div>
                <Tooltip type="light" />

                <Table
                  columns={columns}
                  data={contacts}
                  loading={contactsLoading}
                  filters={filters}
                  sorting={sorting}
                  pageSize={pageSize}
                  setPageSize={setPageSize}
                  loadData={(page) => getContacts(page)}
                  withSearchBar
                  variant="v2"
                  emptyText="No results"
                  mobileNoWrap
                />
              </div>

              {selectedContacts.length > 0 && (
                <div className="bulk-actions">
                  <div className="bulk-actions__left">
                    {selectedContacts.length} <span className="hidden-xs">selected</span>
                  </div>
                  <div className="bulk-actions__right">
                    <Button variant="hollow" onClick={() => setSelectedContacts([])}>
                      Cancel
                    </Button>

                    {isProspects() && permissions.manage_contacts && (
                      <Dropdown
                        options={prospectStatuses}
                        onSelect={(option) => {
                          bulkChangeStatus(option);
                        }}
                        buttonVariant="outline-primary"
                        position="top"
                        fixed
                      >
                        Set status
                      </Dropdown>
                    )}

                    {permissions.delete_contacts && (
                      <Button variant="outline-primary" onClick={() => confirmDeleteContacts()}>
                        <i className="icon-archive"></i>
                        <span className="margin-left-8">Archive</span>
                      </Button>
                    )}

                    {!isMembers() && permissions.manage_contacts && (
                      <Button variant="outline-primary" onClick={bulkConvertToMembers}>
                        Convert to member
                      </Button>
                    )}
                    {isNac() && permissions.manage_contacts && (
                      <Button variant="outline-primary" onClick={bulkConvertToProspects}>
                        Convert to prospect
                      </Button>
                    )}

                    {!isNac() && permissions.manage_contacts && (
                      <Button variant="outline-primary" onClick={bulkConvertToNacs}>
                        Convert to NAC
                      </Button>
                    )}

                    {permissions.send_bulk_comms && (
                      <Button
                        variant="outline-primary"
                        onClick={() => sendBulkMessage(selectedContacts)}
                      >
                        <div className="flex align-center">
                          <Icon name="message" width={19} height={18} color="#F26F21" />
                          <span className="margin-left-8">Send message</span>
                        </div>
                      </Button>
                    )}
                  </div>
                </div>
              )}
            </>
          ) : null
        ) : (
          <TableSettings
            close={() => togglePageSettings(false)}
            settings={pageSettings}
            page={getPageId()}
          />
        )}
      </div>
      <div id="modal-root">
        <ModalContainer onOpen={setModalHandler} />
      </div>
    </div>
  );
};

angular.module('gym').value('ContactsList', ContactsList);
